import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Shared primitives for the ATOZAS Workspace modules.
 *
 * These are Tailwind-styled but resolve colour from the same CSS custom
 * properties the legacy mail UI uses, so every theme preset (and dark mode)
 * applies without any per-component work.
 */

// ------------------------------------------------------------------ helpers

export const cx = (...parts) => parts.filter(Boolean).join(' ');

const BUTTON_VARIANTS = {
  primary:
    'bg-accent text-white border-transparent hover:brightness-110 active:brightness-95 shadow-sm',
  secondary:
    'bg-panel text-ink border-hairline hover:bg-muted active:brightness-95',
  ghost: 'bg-transparent text-ink border-transparent hover:bg-muted',
  subtle: 'bg-muted text-ink border-transparent hover:brightness-95',
  danger: 'bg-red-600 text-white border-transparent hover:bg-red-700',
};

const BUTTON_SIZES = {
  sm: 'h-8 px-3 text-[13px] gap-1.5',
  md: 'h-9 px-4 text-sm gap-2',
  lg: 'h-11 px-5 text-[15px] gap-2',
  icon: 'h-9 w-9 p-0 justify-center',
  'icon-sm': 'h-8 w-8 p-0 justify-center text-[13px]',
};

// ------------------------------------------------------------------ Button

export const Button = React.forwardRef(function Button(
  { variant = 'secondary', size = 'md', className, type = 'button', children, ...rest },
  ref
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cx(
        'ws-focus inline-flex items-center justify-center rounded-lg border font-medium',
        'transition-[filter,background-color,box-shadow] duration-150 select-none',
        'disabled:opacity-50 disabled:pointer-events-none whitespace-nowrap',
        BUTTON_VARIANTS[variant] || BUTTON_VARIANTS.secondary,
        BUTTON_SIZES[size] || BUTTON_SIZES.md,
        className
      )}
      {...rest}
    >
      {children}
    </button>
  );
});

/** Compact square button for editor toolbars, with a pressed state. */
export const ToolbarButton = React.forwardRef(function ToolbarButton(
  { active = false, title, className, children, ...rest },
  ref
) {
  return (
    <button
      ref={ref}
      type="button"
      title={title}
      aria-label={title}
      aria-pressed={active}
      className={cx(
        'ws-focus inline-flex h-8 min-w-8 items-center justify-center rounded-md px-1.5',
        'text-[13px] font-medium transition-colors duration-100',
        'disabled:opacity-40 disabled:pointer-events-none',
        active ? 'bg-accent-soft text-accent' : 'text-ink hover:bg-muted',
        className
      )}
      {...rest}
    >
      {children}
    </button>
  );
});

export const ToolbarDivider = () => (
  <span className="mx-1 inline-block h-5 w-px shrink-0 bg-hairline" aria-hidden="true" />
);

// ------------------------------------------------------------------- Input

export const Input = React.forwardRef(function Input({ className, ...rest }, ref) {
  return (
    <input
      ref={ref}
      className={cx(
        'ws-focus h-9 w-full rounded-lg border border-hairline bg-panel px-3 text-sm text-ink',
        'placeholder:text-ink-soft/70 transition-shadow',
        className
      )}
      {...rest}
    />
  );
});

export const Textarea = React.forwardRef(function Textarea({ className, ...rest }, ref) {
  return (
    <textarea
      ref={ref}
      className={cx(
        'ws-focus w-full resize-y rounded-lg border border-hairline bg-panel px-3 py-2 text-sm text-ink',
        'placeholder:text-ink-soft/70 leading-relaxed',
        className
      )}
      {...rest}
    />
  );
});

export const Select = React.forwardRef(function Select({ className, children, ...rest }, ref) {
  return (
    <select
      ref={ref}
      className={cx(
        'ws-focus h-9 rounded-lg border border-hairline bg-panel px-2 text-sm text-ink',
        className
      )}
      {...rest}
    >
      {children}
    </select>
  );
});

export const Field = ({ label, hint, htmlFor, children, className }) => (
  <label htmlFor={htmlFor} className={cx('block', className)}>
    {label && <span className="mb-1.5 block text-[13px] font-medium text-ink">{label}</span>}
    {children}
    {hint && <span className="mt-1 block text-xs text-ink-soft">{hint}</span>}
  </label>
);

/** Accessible on/off switch used throughout Settings. */
export const Toggle = ({ checked, onChange, label, description, disabled }) => {
  const id = useId();
  return (
    <div className="flex items-start justify-between gap-4 py-2.5">
      <div className="min-w-0">
        <label htmlFor={id} className="block cursor-pointer text-sm font-medium text-ink">
          {label}
        </label>
        {description && <p className="mt-0.5 text-xs leading-relaxed text-ink-soft">{description}</p>}
      </div>
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={!!checked}
        aria-label={label}
        disabled={disabled}
        onClick={() => onChange?.(!checked)}
        className={cx(
          'ws-focus relative mt-0.5 h-6 w-11 shrink-0 rounded-full transition-colors duration-200',
          'disabled:opacity-50 disabled:pointer-events-none',
          checked ? 'bg-accent' : 'bg-ink-soft/30'
        )}
      >
        <span
          className={cx(
            'absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform duration-200',
            checked ? 'translate-x-[22px]' : 'translate-x-0.5'
          )}
        />
      </button>
    </div>
  );
};

// ------------------------------------------------------------------- Cards

export const Card = ({ className, children, ...rest }) => (
  <div
    className={cx('rounded-xl border border-hairline bg-panel shadow-sm', className)}
    {...rest}
  >
    {children}
  </div>
);

export const SectionHeading = ({ title, action, className }) => (
  <div className={cx('mb-3 flex items-center justify-between gap-3', className)}>
    <h2 className="text-[15px] font-semibold tracking-tight text-ink">{title}</h2>
    {action}
  </div>
);

export const Badge = ({ tone = 'neutral', className, children }) => {
  const tones = {
    neutral: 'bg-muted text-ink-soft',
    accent: 'bg-accent-soft text-accent',
    success: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
    warning: 'bg-amber-500/15 text-amber-600 dark:text-amber-400',
    danger: 'bg-red-500/15 text-red-600 dark:text-red-400',
  };
  return (
    <span
      className={cx(
        'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium',
        tones[tone] || tones.neutral,
        className
      )}
    >
      {children}
    </span>
  );
};

// ------------------------------------------------------------------ States

export const Spinner = ({ className }) => (
  <span
    role="status"
    aria-label="Loading"
    className={cx(
      'inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent',
      className
    )}
  />
);

export const LoadingState = ({ label = 'Loading…' }) => (
  <div className="flex flex-col items-center justify-center gap-3 py-16 text-ink-soft">
    <Spinner className="h-6 w-6 text-accent" />
    <p className="text-sm">{label}</p>
  </div>
);

export const ErrorState = ({ message, onRetry }) => (
  <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
    <span className="text-3xl" aria-hidden="true">
      ⚠️
    </span>
    <p className="max-w-md text-sm text-ink">{message || 'Something went wrong'}</p>
    {onRetry && (
      <Button variant="secondary" size="sm" onClick={onRetry}>
        Try again
      </Button>
    )}
  </div>
);

export const EmptyState = ({ icon = '📭', title, description, action }) => (
  <div className="flex flex-col items-center justify-center gap-2 px-6 py-20 text-center">
    <span className="mb-1 text-4xl opacity-80" aria-hidden="true">
      {icon}
    </span>
    <h3 className="text-base font-semibold text-ink">{title}</h3>
    {description && <p className="max-w-sm text-sm leading-relaxed text-ink-soft">{description}</p>}
    {action && <div className="mt-3">{action}</div>}
  </div>
);

// ------------------------------------------------------------------- Modal

/**
 * Portalled modal with focus containment, Escape-to-close and a scroll lock.
 */
export const Modal = ({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  closeOnBackdrop = true,
}) => {
  const panelRef = useRef(null);
  const previouslyFocused = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    previouslyFocused.current = document.activeElement;
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose?.();
        return;
      }
      if (event.key !== 'Tab' || !panelRef.current) return;

      const focusable = panelRef.current.querySelectorAll(
        'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    const timer = window.setTimeout(() => {
      const target = panelRef.current?.querySelector('[data-autofocus]') || panelRef.current;
      target?.focus?.();
    }, 30);

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.body.style.overflow = overflow;
      window.clearTimeout(timer);
      previouslyFocused.current?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;

  const sizes = {
    sm: 'max-w-sm',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    full: 'max-w-6xl',
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[2000] flex items-center justify-center p-4"
      role="presentation"
      onMouseDown={(event) => {
        if (closeOnBackdrop && event.target === event.currentTarget) onClose?.();
      }}
    >
      <div className="absolute inset-0 bg-slate-900/45 backdrop-blur-[2px]" aria-hidden="true" />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        tabIndex={-1}
        className={cx(
          'ws-glass relative flex max-h-[88vh] w-full flex-col overflow-hidden rounded-2xl',
          'animate-ws-pop outline-none',
          sizes[size] || sizes.md
        )}
      >
        {(title || description) && (
          <div className="flex items-start justify-between gap-4 border-b border-hairline px-5 py-4">
            <div className="min-w-0">
              {title && <h2 className="truncate text-base font-semibold text-ink">{title}</h2>}
              {description && <p className="mt-0.5 text-[13px] text-ink-soft">{description}</p>}
            </div>
            <ToolbarButton title="Close" onClick={onClose}>
              ✕
            </ToolbarButton>
          </div>
        )}
        <div className="ws-scroll flex-1 px-5 py-4">{children}</div>
        {footer && (
          <div className="flex items-center justify-end gap-2 border-t border-hairline px-5 py-3">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

/** Confirm dialog. `tone="danger"` for destructive actions. */
export const ConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  title = 'Are you sure?',
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'primary',
  busy = false,
  // Optional third choice, e.g. "This occurrence" next to "Delete series".
  extraAction,
}) => (
  <Modal
    open={open}
    onClose={onClose}
    title={title}
    size="sm"
    footer={
      <>
        <Button variant="ghost" onClick={onClose} disabled={busy}>
          {cancelLabel}
        </Button>
        {extraAction && (
          <Button variant="secondary" onClick={extraAction.onClick} disabled={busy}>
            {extraAction.label}
          </Button>
        )}
        <Button variant={tone === 'danger' ? 'danger' : 'primary'} onClick={onConfirm} disabled={busy}>
          {busy ? <Spinner /> : confirmLabel}
        </Button>
      </>
    }
  >
    <p className="text-sm leading-relaxed text-ink-soft">{message}</p>
  </Modal>
);

// ------------------------------------------------------------------- Menu

/**
 * Lightweight dropdown menu anchored to its trigger.
 * `items` entries: { id, label, icon?, onClick, danger?, disabled?, divider? }
 */
export const Menu = ({ trigger, items = [], align = 'right', className }) => {
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState(null);
  const wrapRef = useRef(null);
  const panelRef = useRef(null);

  /**
   * Position the panel from the trigger's viewport rect.
   *
   * The panel is portaled to <body> rather than positioned inside the wrapper:
   * list rows, document cards and modal panels all use `overflow-hidden`, which
   * clipped the old absolutely positioned dropdown so only its first item was
   * visible. Flips above the trigger when there is not enough room below, and is
   * clamped to the viewport on both axes.
   */
  const place = useCallback(() => {
    if (!wrapRef.current) return;
    const rect = wrapRef.current.getBoundingClientRect();
    const width = panelRef.current?.offsetWidth || 190;
    const height = panelRef.current?.offsetHeight || 0;
    const gap = 4;
    const margin = 8;

    let left = align === 'right' ? rect.right - width : rect.left;
    left = Math.min(Math.max(left, margin), Math.max(window.innerWidth - width - margin, margin));

    const spaceBelow = window.innerHeight - rect.bottom - gap - margin;
    const spaceAbove = rect.top - gap - margin;
    const flip = height > spaceBelow && spaceAbove > spaceBelow;

    setPlacement({
      left,
      top: flip ? Math.max(rect.top - height - gap, margin) : rect.bottom + gap,
      maxHeight: Math.max(flip ? spaceAbove : spaceBelow, 140),
    });
  }, [align]);

  // Measured after the panel mounts (it renders hidden offscreen for one frame).
  useLayoutEffect(() => {
    if (!open) {
      setPlacement(null);
      return undefined;
    }
    place();

    // `true` captures scrolls from inner containers such as .ws-scroll.
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, place]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (event) => {
      // The panel lives outside the wrapper now, so both need checking —
      // otherwise mousedown would unmount it before a menu item's click landed.
      if (wrapRef.current?.contains(event.target)) return;
      if (panelRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    const onKey = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const panel = (
    <div
      ref={panelRef}
      role="menu"
      className={cx(
        'ws-glass ws-scroll fixed z-[2600] min-w-[190px] rounded-xl py-1',
        placement && 'animate-ws-fade-up'
      )}
      style={{
        left: placement ? placement.left : -9999,
        top: placement ? placement.top : -9999,
        maxHeight: placement?.maxHeight,
        visibility: placement ? 'visible' : 'hidden',
      }}
    >
      {items.filter(Boolean).map((item, index) =>
        item.divider ? (
          <div key={`d${index}`} className="my-1 h-px bg-hairline" />
        ) : (
          <button
            key={item.id || item.label}
            type="button"
            role="menuitem"
            disabled={item.disabled}
            onClick={(event) => {
              event.stopPropagation();
              setOpen(false);
              item.onClick?.(event);
            }}
            className={cx(
              'flex w-full items-center gap-2.5 px-3 py-2 text-left text-[13px]',
              'transition-colors disabled:opacity-40 disabled:pointer-events-none',
              item.danger ? 'text-red-600 hover:bg-red-500/10' : 'text-ink hover:bg-muted'
            )}
          >
            {item.icon && (
              <span className="w-4 text-center" aria-hidden="true">
                {item.icon}
              </span>
            )}
            <span className="flex-1 truncate">{item.label}</span>
            {item.shortcut && <span className="text-[11px] text-ink-soft">{item.shortcut}</span>}
          </button>
        )
      )}
    </div>
  );

  return (
    <div ref={wrapRef} className={cx('relative inline-flex', className)}>
      {React.cloneElement(trigger, {
        onClick: (event) => {
          event.stopPropagation();
          setOpen((v) => !v);
          trigger.props.onClick?.(event);
        },
        'aria-haspopup': 'menu',
        'aria-expanded': open,
      })}
      {open && createPortal(panel, document.body)}
    </div>
  );
};

// ------------------------------------------------------------------ Toasts

const ToastContext = React.createContext(() => {});

export const useToast = () => React.useContext(ToastContext);

/**
 * Toast host. Wrap a module in this and call `useToast()` to push messages.
 */
export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const counter = useRef(0);

  const dismiss = useCallback((id) => {
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (message, options = {}) => {
      counter.current += 1;
      const id = counter.current;
      const toast = {
        id,
        message,
        tone: options.tone || 'info',
        action: options.action || null,
      };
      setToasts((list) => [...list, toast]);
      if (options.duration !== 0) {
        window.setTimeout(() => dismiss(id), options.duration || 4000);
      }
      return id;
    },
    [dismiss]
  );

  const tones = {
    info: 'border-hairline',
    success: 'border-emerald-500/40',
    warning: 'border-amber-500/40',
    error: 'border-red-500/40',
  };
  const icons = { info: 'ℹ️', success: '✓', warning: '⚠️', error: '✕' };

  return (
    <ToastContext.Provider value={push}>
      {children}
      <div
        aria-live="polite"
        className="pointer-events-none fixed bottom-5 left-1/2 z-[3000] flex w-full max-w-md -translate-x-1/2 flex-col items-center gap-2 px-4"
      >
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={cx(
              'ws-glass pointer-events-auto flex w-full items-center gap-3 rounded-xl border px-4 py-2.5',
              'animate-ws-fade-up text-sm text-ink',
              tones[toast.tone]
            )}
          >
            <span aria-hidden="true">{icons[toast.tone]}</span>
            <span className="flex-1">{toast.message}</span>
            {toast.action && (
              <button
                type="button"
                onClick={() => {
                  toast.action.onClick();
                  dismiss(toast.id);
                }}
                className="ws-focus rounded px-2 py-1 text-[13px] font-semibold text-accent hover:bg-accent-soft"
              >
                {toast.action.label}
              </button>
            )}
            <button
              type="button"
              aria-label="Dismiss"
              onClick={() => dismiss(toast.id)}
              className="ws-focus rounded px-1 text-ink-soft hover:text-ink"
            >
              ✕
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

// ------------------------------------------------------------------ Layout

/** Standard page frame: sticky header row plus a scrolling body. */
export const WorkspacePage = ({ children, className }) => (
  <div className={cx('ws-shell', className)}>{children}</div>
);

export const WorkspaceHeader = ({ icon, title, subtitle, children, onBack }) => (
  <header className="flex flex-wrap items-center gap-3 border-b border-hairline bg-panel/80 px-4 py-3 backdrop-blur-sm sm:px-6">
    {onBack && (
      <ToolbarButton title="Back" onClick={onBack}>
        ←
      </ToolbarButton>
    )}
    {icon && (
      <span className="text-xl leading-none" aria-hidden="true">
        {icon}
      </span>
    )}
    <div className="min-w-0 flex-1">
      <h1 className="truncate text-lg font-semibold tracking-tight text-ink">{title}</h1>
      {subtitle && <p className="truncate text-[13px] text-ink-soft">{subtitle}</p>}
    </div>
    <div className="flex flex-wrap items-center gap-2">{children}</div>
  </header>
);

export const WorkspaceBody = ({ children, className, padded = true }) => (
  <div className={cx('ws-scroll flex-1', padded && 'px-4 py-5 sm:px-6', className)}>{children}</div>
);

/** Horizontal filter chips used by list views. */
export const FilterTabs = ({ tabs = [], value, onChange, className }) => (
  <div className={cx('flex flex-wrap items-center gap-1.5', className)} role="tablist">
    {tabs.map((tab) => (
      <button
        key={tab.id}
        type="button"
        role="tab"
        aria-selected={value === tab.id}
        onClick={() => onChange(tab.id)}
        className={cx(
          'ws-focus inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[13px] font-medium transition-colors',
          value === tab.id
            ? 'border-transparent bg-accent text-white'
            : 'border-hairline bg-panel text-ink-soft hover:bg-muted hover:text-ink'
        )}
      >
        {tab.icon && <span aria-hidden="true">{tab.icon}</span>}
        {tab.label}
        {tab.count > 0 && (
          <span
            className={cx(
              'ml-0.5 rounded-full px-1.5 text-[11px]',
              value === tab.id ? 'bg-white/25' : 'bg-muted'
            )}
          >
            {tab.count}
          </span>
        )}
      </button>
    ))}
  </div>
);

/** Search box with a clear button. */
export const SearchInput = ({ value, onChange, placeholder = 'Search…', className, autoFocus }) => (
  <div className={cx('relative', className)}>
    <span
      className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-ink-soft"
      aria-hidden="true"
    >
      🔍
    </span>
    <input
      type="search"
      value={value}
      autoFocus={autoFocus}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      className="ws-focus h-9 w-full rounded-full border border-hairline bg-panel pl-9 pr-9 text-sm text-ink placeholder:text-ink-soft/70"
    />
    {value && (
      <button
        type="button"
        aria-label="Clear search"
        onClick={() => onChange('')}
        className="ws-focus absolute right-2.5 top-1/2 -translate-y-1/2 rounded-full px-1 text-ink-soft hover:text-ink"
      >
        ✕
      </button>
    )}
  </div>
);

/** Inline "Saved / Saving…" indicator for autosaving editors. */
export const SaveIndicator = ({ status, savedAt }) => {
  const label = {
    saving: 'Saving…',
    saved: savedAt
      ? `Saved ${new Date(savedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
      : 'Saved',
    error: 'Not saved',
    dirty: 'Unsaved changes',
  }[status];

  if (!label) return null;

  return (
    <span
      className={cx(
        'inline-flex items-center gap-1.5 text-[12px]',
        status === 'error' ? 'text-red-500' : 'text-ink-soft'
      )}
    >
      {status === 'saving' && <Spinner className="h-3 w-3" />}
      {label}
    </span>
  );
};
